import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CalendarIcon,
  CheckCircledIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { Avatar } from '../../components';
import { KanbanTask, TaskPriority } from '../../types/kanban';
import { formatDueDateDisplay } from '../../utils/taskDate';
import { getLabelChipStyle } from '../../utils/taskLabel';
import './KanbanTaskCard.scss';

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  high: 'HIGH PRIORITY',
  normal: 'NORMAL',
  low: 'LOW PRIORITY',
};

interface KanbanTaskCardProps {
  task: KanbanTask;
  isDragging?: boolean;
}

export const KanbanTaskCard: React.FC<KanbanTaskCardProps> = ({
  task,
  isDragging = false,
}) => {
  const dueLabel = formatDueDateDisplay(task.dueDate);
  const isCompleted = Boolean(task.isClosed) || task.columnId === 'completed';

  return (
    <article
      className={[
        'kanban-task-card',
        isCompleted && 'kanban-task-card--completed',
        isDragging && 'kanban-task-card--dragging',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {task.priority && (
        <span
          className={`kanban-task-card__priority kanban-task-card__priority--${task.priority}`}
        >
          {PRIORITY_LABEL[task.priority]}
        </span>
      )}

      <h3 className="kanban-task-card__title">{task.title}</h3>
      {!isCompleted && (task.leadUniqueId || task.taskNumber) && (
        <p className="kanban-task-card__ticket">
          {task.leadUniqueId ?? task.taskNumber}
        </p>
      )}
      <p className="kanban-task-card__email">{task.email}</p>

      {(task.labels.length > 0 || task.typeName) && (
        <div className="kanban-task-card__tags">
          {task.labels.map((label) => (
            <span
              key={label.labelId}
              className="kanban-task-card__tag kanban-task-card__tag--dynamic"
              style={isCompleted ? undefined : getLabelChipStyle(label.colorHex)}
            >
              {label.name}
            </span>
          ))}
          {task.typeName && (
            <span className="kanban-task-card__tag kanban-task-card__tag--source">
              {task.typeName}
            </span>
          )}
        </div>
      )}

      <div className="kanban-task-card__footer">
        <div
          className={`kanban-task-card__date ${
            isCompleted ? 'kanban-task-card__date--closed' : ''
          }`}
        >
          {isCompleted ? (
            <>
              <CheckCircledIcon className="kanban-task-card__closed-icon" />
              <span className="kanban-task-card__closed-text">Closed</span>
            </>
          ) : (
            <>
              <CalendarIcon aria-hidden="true" />
              <span>{dueLabel}</span>
            </>
          )}
        </div>
        {task.assignee ? (
          <Avatar
            name={task.assignee.name}
            src={task.assignee.avatar}
            size="small"
            className={isCompleted ? 'kanban-task-card__avatar--muted' : undefined}
          />
        ) : (
          !isCompleted && (
            <span className="kanban-task-card__unassigned" aria-label="Unassigned">
              <PersonIcon />
            </span>
          )
        )}
      </div>
    </article>
  );
};

interface KanbanSortableTaskProps {
  task: KanbanTask;
  onOpen?: (task: KanbanTask) => void;
}

export const KanbanSortableTask: React.FC<KanbanSortableTaskProps> = ({
  task,
  onOpen,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.taskId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = useCallback(() => {
    if (!isDragging && onOpen) {
      onOpen(task);
    }
  }, [isDragging, onOpen, task]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kanban-sortable-task"
      {...attributes}
      {...listeners}
      onClick={handleClick}
    >
      <KanbanTaskCard task={task} isDragging={isDragging} />
    </div>
  );
};
